import React from 'react';
import { Typography, Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip } from '@mui/material';

const TypographyExample = ({ variant, children, description }) => {
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        border: '1px solid', 
        borderColor: 'gray.g200',
        mb: 2 
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant={variant}>
            {children}
          </Typography>
          <Chip 
            label={variant} 
            size="small" 
            variant="outlined" 
            sx={{ fontFamily: 'monospace' }}
          />
        </Stack>
        {description && (
          <Typography variant="body2" color="gray.g500">
            {description}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const Typography_Page = () => {
  const typographySpecs = [
    { variant: 'h1', size: '2.5rem', weight: 700, lineHeight: 1.2, usage: 'Page titles, hero headings' },
    { variant: 'h2', size: '2rem', weight: 700, lineHeight: 1.3, usage: 'Section headings' },
    { variant: 'h3', size: '1.75rem', weight: 600, lineHeight: 1.3, usage: 'Subsection headings' },
    { variant: 'h4', size: '1.5rem', weight: 600, lineHeight: 1.4, usage: 'Component titles' },
    { variant: 'h5', size: '1.25rem', weight: 500, lineHeight: 1.4, usage: 'Small headings' },
    { variant: 'h6', size: '1.125rem', weight: 500, lineHeight: 1.4, usage: 'Smallest headings' },
    { variant: 'body1', size: '1rem', weight: 400, lineHeight: 1.6, usage: 'Primary body text' },
    { variant: 'body2', size: '0.875rem', weight: 400, lineHeight: 1.5, usage: 'Secondary body text' },
    { variant: 'caption', size: '0.75rem', weight: 400, lineHeight: 1.4, usage: 'Small labels, captions' },
  ];

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Typography
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Our typography system uses the Almarai font family to ensure consistency and 
          readability across Arabic and English content.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Almarai Font" variant="outlined" />
          <Chip label="Responsive Sizes" variant="outlined" />
          <Chip label="Semantic Hierarchy" variant="outlined" />
        </Stack>
      </Stack>

      {/* Typography Examples */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Typography Scale
        </Typography>
        
        <TypographyExample 
          variant="h1" 
          description="Used for main page titles and hero headings"
        >
          Main Page Heading
        </TypographyExample>

        <TypographyExample 
          variant="h2" 
          description="Used for major section headings"
        >
          Section Heading
        </TypographyExample>

        <TypographyExample 
          variant="h3" 
          description="Used for subsection headings"
        >
          Subsection Heading
        </TypographyExample>

        <TypographyExample 
          variant="h4" 
          description="Used for component titles"
        >
          Component Title
        </TypographyExample>

        <TypographyExample 
          variant="h5" 
          description="Used for smaller headings"
        >
          Small Heading
        </TypographyExample>

        <TypographyExample 
          variant="h6" 
          description="Used for the smallest headings"
        >
          Smallest Heading
        </TypographyExample>

        <TypographyExample 
          variant="body1" 
          description="Primary body text for paragraphs and content"
        >
          This is the primary body text used for main content, paragraphs, and general reading. 
          It provides optimal readability with proper line height and spacing.
        </TypographyExample>

        <TypographyExample 
          variant="body2" 
          description="Secondary body text for supporting content"
        >
          This is secondary body text used for supporting information, descriptions, 
          and less prominent content areas.
        </TypographyExample>

        <TypographyExample 
          variant="caption" 
          description="Small text for labels and captions"
        >
          Caption text for small labels, metadata, and image captions
        </TypographyExample>
      </Box>

      {/* Typography Specifications Table */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Typography Specifications
        </Typography>
        
        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'gray.g50' }}>
                <TableCell><strong>Variant</strong></TableCell>
                <TableCell><strong>Size</strong></TableCell>
                <TableCell><strong>Weight</strong></TableCell>
                <TableCell><strong>Line Height</strong></TableCell>
                <TableCell><strong>Usage</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {typographySpecs.map((spec) => (
                <TableRow key={spec.variant}>
                  <TableCell>
                    <code style={{ 
                      backgroundColor: '#f5f5f5', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}>
                      {spec.variant}
                    </code>
                  </TableCell>
                  <TableCell>{spec.size}</TableCell>
                  <TableCell>{spec.weight}</TableCell>
                  <TableCell>{spec.lineHeight}</TableCell>
                  <TableCell>{spec.usage}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Font Information */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Font Information
        </Typography>
        
        <Stack spacing={3}>
          <Paper sx={{ p: 3, backgroundColor: 'gray.g25' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Primary Font: Almarai
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Almarai is a contemporary Arabic typeface designed for digital interfaces. 
              It provides excellent readability in both Arabic and Latin scripts.
            </Typography>
            <Typography variant="body2">
              <strong>Fallback:</strong> Arial, sans-serif
            </Typography>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Font Weights Available
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Typography variant="body1" sx={{ fontWeight: 400, minWidth: 200 }}>
                  Regular (400)
                </Typography>
                <Typography variant="body2" color="gray.g500">
                  Standard body text
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 200 }}>
                  Medium (500)
                </Typography>
                <Typography variant="body2" color="gray.g500">
                  Emphasis, small headings
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 200 }}>
                  Semi Bold (600)
                </Typography>
                <Typography variant="body2" color="gray.g500">
                  Subheadings, important text
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Typography variant="body1" sx={{ fontWeight: 700, minWidth: 200 }}>
                  Bold (700)
                </Typography>
                <Typography variant="body2" color="gray.g500">
                  Main headings, strong emphasis
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {/* Usage Guidelines */}
      <Paper sx={{ p: 4, backgroundColor: 'primary.light' }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
          Typography Guidelines
        </Typography>
        <Stack spacing={2}>
          <Typography variant="body2">
            <strong>Hierarchy:</strong> Use heading levels (H1-H6) in order to maintain proper content hierarchy.
          </Typography>
          <Typography variant="body2">
            <strong>Contrast:</strong> Ensure sufficient color contrast between text and background colors.
          </Typography>
          <Typography variant="body2">
            <strong>Line Length:</strong> Keep line lengths between 45-75 characters for optimal readability.
          </Typography>
          <Typography variant="body2">
            <strong>Spacing:</strong> Use consistent spacing between typography elements to create visual rhythm.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Typography_Page;